import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, AlertCircle, Eye, Clock, MapPin, Filter, Scale, TrendingDown, Users, ShieldAlert } from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const AttentionCenter = ({ 
  criticalHives = [], 
  watchlistHives = [],
  overdueHives = [],
  weightAlerts = [],
  yards = [],
  onInspect,
  onAssignCrew,
  onConfirmDestroyed
}) => {
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState({
    critical: true,
    watchlist: false,
    overdue: true,
    weight: true
  });
  const [selectedYardFilter, setSelectedYardFilter] = useState('all');
  const [groupByYard, setGroupByYard] = useState(false);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Get unique yards from all hives
  const uniqueYards = useMemo(() => {
    const yardsSet = new Set();
    [...criticalHives, ...watchlistHives, ...overdueHives].forEach(hive => {
      if (hive.yard) yardsSet.add(hive.yard);
    });
    return Array.from(yardsSet).sort();
  }, [criticalHives, watchlistHives, overdueHives]);

  // Filter hives by selected yard
  const filteredCritical = useMemo(() => {
    if (selectedYardFilter === 'all') return criticalHives;
    return criticalHives.filter(h => h.yard === selectedYardFilter);
  }, [criticalHives, selectedYardFilter]);

  const filteredWatchlist = useMemo(() => {
    if (selectedYardFilter === 'all') return watchlistHives;
    return watchlistHives.filter(h => h.yard === selectedYardFilter);
  }, [watchlistHives, selectedYardFilter]);

  const filteredOverdue = useMemo(() => {
    if (selectedYardFilter === 'all') return overdueHives;
    return overdueHives.filter(h => h.yard === selectedYardFilter);
  }, [overdueHives, selectedYardFilter]);

  const filteredWeightAlerts = useMemo(() => {
    if (selectedYardFilter === 'all') return weightAlerts;
    return weightAlerts.filter(h => h.apiary_name === selectedYardFilter);
  }, [weightAlerts, selectedYardFilter]);

  // Group hives by yard
  const groupHivesByYard = (hives) => {
    const grouped = {};
    hives.forEach(hive => {
      const yard = hive.yard || 'Unassigned';
      if (!grouped[yard]) grouped[yard] = [];
      grouped[yard].push(hive);
    });
    return grouped;
  };

  // Render hive card
  const renderHiveCard = (hive, type = 'critical', idx) => {
    const bgColors = {
      critical: 'bg-red-50 border-red-200 hover:bg-red-100',
      watchlist: 'bg-amber-50 border-amber-200 hover:bg-amber-100',
      overdue: 'bg-orange-50 border-orange-200 hover:bg-orange-100'
    };
    const textColors = {
      critical: 'text-red-700',
      watchlist: 'text-amber-700',
      overdue: 'text-orange-700'
    };
    const icons = {
      critical: '🚨',
      watchlist: '👁️',
      overdue: '⏰'
    };
    const buttonColors = {
      critical: 'bg-orange-500 hover:bg-orange-600',
      overdue: 'bg-stone-200 hover:bg-stone-300 text-black'
    };

    return (
      <div 
        key={hive.id || idx}
        className={`${bgColors[type]} border rounded-lg p-3 cursor-pointer transition-colors`}
        onClick={() => navigate(`/?view=hive-detail&hiveId=${hive.id}`)}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={textColors[type].replace('700', '600')}>{icons[type]}</span>
              <span className="font-semibold text-black">Hive {hive.hive_number}</span>
              {!groupByYard && hive.yard && (
                <span className="text-stone-500 text-sm flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {hive.yard}
                </span>
              )}
            </div>
            {/* Alert details */}
            <div className={`text-sm ${textColors[type]} mt-2`}>
              {type === 'critical' && (
                hive.alerts?.map((a, i) => (
                  <div key={i} className="flex items-center gap-1">
                    <span>•</span>
                    <span>{a.label}</span>
                  </div>
                )) || <span>{hive.reason}</span>
              )}
              {type === 'watchlist' && (
                <span>{hive.watchReason || 'Under observation'}</span>
              )}
              {type === 'overdue' && (
                <span>
                  {hive.daysSinceInspection === null 
                    ? 'Not yet inspected' 
                    : `No inspection in ${hive.daysSinceInspection} days`}
                </span>
              )}
            </div>
          </div>
          <div className="text-xs text-stone-500 flex items-center gap-1 flex-shrink-0">
            {hive.daysSinceInspection && hive.daysSinceInspection !== null && type !== 'overdue' && (
              <span>{hive.daysSinceInspection} days ago</span>
            )}
            {type === 'overdue' && hive.daysOverdue !== null && hive.daysOverdue > 0 && (
              <span className="text-orange-600 font-medium bg-orange-100 px-2 py-1 rounded-full">
                {hive.daysOverdue} days overdue
              </span>
            )}
            {type === 'overdue' && hive.daysSinceInspection === null && (
              <span className="text-amber-600 font-medium bg-amber-100 px-2 py-1 rounded-full">
                Never inspected
              </span>
            )}
            <ChevronRight className="h-4 w-4" />
          </div>
        </div>
        {/* Action button */}
        {(type === 'critical' || type === 'overdue') && (
          <div className="flex gap-2 mt-3">
            <Button
              size="sm"
              onClick={(e) => { 
                e.stopPropagation(); 
                navigate(`/?view=new-inspection&hiveId=${hive.id}`);
              }}
              className={`h-8 px-3 text-xs font-semibold ${buttonColors[type]} text-white`}
              data-testid={`inspect-${type}-${hive.id}`}
            >
              Inspect Now
            </Button>
            {type === 'critical' && hive.alerts?.some(a => a.type === 'afb') && hive.status === 'inactive' && onConfirmDestroyed && (
              <Button
                size="sm"
                onClick={(e) => { 
                  e.stopPropagation(); 
                  onConfirmDestroyed(hive.id);
                }}
                className="h-8 px-3 text-xs font-semibold bg-red-600 hover:bg-red-700 text-white"
                data-testid={`confirm-destroyed-alert-${hive.id}`}
              >
                <ShieldAlert className="h-3 w-3 mr-1" />
                Confirm Destroyed
              </Button>
            )}
            {onAssignCrew && (
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => { 
                  e.stopPropagation(); 
                  onAssignCrew(hive);
                }}
                className="h-8 px-2 text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
                data-testid={`assign-crew-${hive.id}`}
              >
                <Users className="h-3 w-3 mr-1" />
                Assign
              </Button>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render section with optional yard grouping
  const renderSection = (title, hives, type, icon, colorClass, expanded, onToggle) => {
    if (hives.length === 0) return null;

    const groupedHives = groupByYard ? groupHivesByYard(hives) : null;

    return (
      <div className={type === 'overdue' ? '' : 'border-b border-stone-100'}>
        <button
          onClick={onToggle}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-stone-50"
        >
          <div className="flex items-center gap-2">
            <div className={`w-6 h-6 rounded-full ${colorClass} flex items-center justify-center`}>
              {icon}
            </div>
            <span className={`font-semibold ${colorClass.replace('bg-', 'text-').replace('-500', '-700')}`}>
              {title} ({hives.length})
            </span>
          </div>
          {expanded ? (
            <ChevronDown className="h-5 w-5 text-stone-400" />
          ) : (
            <ChevronRight className="h-5 w-5 text-stone-400" />
          )}
        </button>
        
        {expanded && (
          <div className="px-4 pb-4">
            {groupByYard && groupedHives ? (
              // Grouped by yard view
              <div className="space-y-4">
                {Object.entries(groupedHives).sort(([a], [b]) => a.localeCompare(b)).map(([yard, yardHives]) => (
                  <div key={yard}>
                    <div className="flex items-center gap-2 mb-2 pb-1 border-b border-stone-200">
                      <MapPin className="h-4 w-4 text-stone-500" />
                      <span className="font-medium text-stone-700">{yard}</span>
                      <span className="text-xs text-stone-400">({yardHives.length})</span>
                    </div>
                    <div className="space-y-2 ml-2">
                      {yardHives.map((hive, idx) => renderHiveCard(hive, type, idx))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Flat list view
              <div className="space-y-3">
                {hives.map((hive, idx) => renderHiveCard(hive, type, idx))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const totalItems = filteredCritical.length + filteredWatchlist.length + filteredOverdue.length + filteredWeightAlerts.length;

  return (
    <div className="bg-white rounded-xl border border-stone-200 shadow-sm" data-testid="attention-center">
      <div className="p-4 border-b border-stone-100">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-bold text-black text-lg">Attention Center</h2>
          
          {/* Filter Controls */}
          {(uniqueYards.length > 1 || criticalHives.length + watchlistHives.length + overdueHives.length > 5) && (
            <div className="flex items-center gap-2 flex-wrap">
              {/* Yard Filter */}
              {uniqueYards.length > 1 && (
                <Select value={selectedYardFilter} onValueChange={setSelectedYardFilter}>
                  <SelectTrigger className="h-8 w-[140px] text-xs border-stone-200">
                    <Filter className="h-3 w-3 mr-1" />
                    <SelectValue placeholder="Filter by yard" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Yards</SelectItem>
                    {uniqueYards.map(yard => (
                      <SelectItem key={yard} value={yard}>{yard}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              {/* Group Toggle */}
              <Button
                variant={groupByYard ? 'default' : 'outline'}
                size="sm"
                onClick={() => setGroupByYard(!groupByYard)}
                className={`h-8 text-xs ${groupByYard ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
              >
                <MapPin className="h-3 w-3 mr-1" />
                Group
              </Button>
            </div>
          )}
        </div>
        
        {/* Active filter indicator */}
        {selectedYardFilter !== 'all' && (
          <div className="mt-2 text-xs text-amber-600 flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            Showing: {selectedYardFilter} ({totalItems} items)
          </div>
        )}
      </div>

      {/* Critical Alerts Section */}
      {renderSection(
        'Critical alerts',
        filteredCritical,
        'critical',
        <AlertCircle className="h-4 w-4 text-white" />,
        'bg-red-500',
        expandedSections.critical,
        () => toggleSection('critical')
      )}

      {/* Watchlist Section */}
      {renderSection(
        'Watchlist',
        filteredWatchlist,
        'watchlist',
        <Eye className="h-4 w-4 text-white" />,
        'bg-amber-500',
        expandedSections.watchlist,
        () => toggleSection('watchlist')
      )}

      {/* Overdue Inspections Section */}
      {renderSection(
        'Overdue inspections',
        filteredOverdue,
        'overdue',
        <Clock className="h-4 w-4 text-white" />,
        'bg-orange-500',
        expandedSections.overdue,
        () => toggleSection('overdue')
      )}

      {/* Weight Loss Alerts Section */}
      {filteredWeightAlerts.length > 0 && (
        <div>
          <button
            onClick={() => toggleSection('weight')}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-stone-50"
          >
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center">
                <Scale className="h-4 w-4 text-white" />
              </div>
              <span className="font-semibold text-purple-700">
                Weight Loss Alerts ({filteredWeightAlerts.length})
              </span>
            </div>
            {expandedSections.weight ? (
              <ChevronDown className="h-5 w-5 text-stone-400" />
            ) : (
              <ChevronRight className="h-5 w-5 text-stone-400" />
            )}
          </button>
          
          {expandedSections.weight && (
            <div className="px-4 pb-4 space-y-3">
              {filteredWeightAlerts.map((alert, idx) => (
                <div 
                  key={alert.hive_id || idx}
                  className="bg-purple-50 border border-purple-200 rounded-lg p-3 cursor-pointer hover:bg-purple-100 transition-colors"
                  onClick={() => navigate(`/?view=hive-detail&hiveId=${alert.hive_id}`)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-purple-600">⚖️</span>
                        <span className="font-semibold text-black">{alert.hive_name}</span>
                        <span className="text-stone-500 text-sm flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {alert.apiary_name}
                        </span>
                      </div>
                      <div className="mt-2 flex items-center gap-4 text-sm text-purple-700">
                        <span>Previous: {alert.previous_weight} kg</span>
                        <span>Current: {alert.current_weight} kg</span>
                      </div>
                    </div>
                    <div className="text-right flex items-center gap-2">
                      <div>
                        <div className="flex items-center gap-1 text-red-600 font-bold">
                          <TrendingDown className="h-4 w-4" />
                          {alert.change_kg} kg
                        </div>
                        <span className="text-xs text-red-500">{alert.change_percent}%</span>
                      </div>
                      <ChevronRight className="h-4 w-4 text-stone-400" />
                    </div>
                  </div>
                  {alert.weight_loss_reasons && alert.weight_loss_reasons.length > 0 ? (
                    <div className="mt-2 bg-amber-50 text-amber-700 text-xs p-2 rounded-lg">
                      <span className="font-medium">Reason: </span>
                      {alert.weight_loss_reasons.join(', ')}
                    </div>
                  ) : (
                    <div className="mt-2 bg-red-50 text-red-700 text-xs p-2 rounded-lg">
                      ⚠️ Possible causes: robbing, starvation, swarm prep, disease
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {totalItems === 0 && (
        <div className="p-8 text-center">
          <div className="text-4xl mb-2">✨</div>
          <p className="text-stone-500 font-medium">
            {selectedYardFilter !== 'all' 
              ? `No items need attention in ${selectedYardFilter}` 
              : 'All clear! No items need attention.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default AttentionCenter;
